import { coachShortDisplayName } from '@efdigital/ll-addon-coaching';
import {
  type CoachingSession,
  type CoachingSessionStatus,
  type CoachingSessionType,
} from '@efdigital/ll-addon-data';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { ReactiveDataDocument } from '@warp-drive/core/reactive';
import { withReactiveResponse } from '@warp-drive/core/request';
import type { RequestInfo } from '@warp-drive/core/types/request';
import BsPopover from 'ember-bootstrap/components/bs-popover';

import {
  type CoachingSessionFilters,
  queryCoachingSessions,
} from '../../data/builders/coaching/coaching-sessions.ts';
import { CoachSearchPicker } from '../coaching/sessions/search-coach.gts';
import { SelectSessionStatusCheckboxList } from '../coaching/sessions/select-session-status.gts';
import { SelectSessionStatusGroup } from '../coaching/sessions/select-session-status-group.gts';
import { SelectSessionType } from '../coaching/sessions/select-session-type.gts';
import { UserPicker } from '../pickers/user-picker.gts';

export interface SearchSessionsState {
  sessionType: CoachingSessionType | '';
  statuses: CoachingSessionStatus[];
  offset: number;
  limit: number;
  coachUserId: string;
  coacheeUserId: string;
}

interface SearchSessionsSignature {
  Args: {
    legend: string;
    state: Readonly<SearchSessionsState>;
    setState: (state: Partial<SearchSessionsState>) => void;
    /**
     * Fixed scope for the page the filters sit on — a coach, a partnership, a
     * plan. Omitted on the unfiltered admin list.
     */
    filters?: CoachingSessionFilters;
  };
  Blocks: {
    query: [RequestInfo<ReactiveDataDocument<CoachingSession[]>>];
  };
  Element: HTMLFieldSetElement;
}

export class SearchSessions extends Component<SearchSessionsSignature> {
  @tracked moreOptionsVisible = false;

  showMoreOptions = () => {
    this.moreOptionsVisible = true;
  };

  get query() {
    // Scope comes from the page (a coach, a partnership, or nothing) and is
    // merged with whatever the filter controls below are set to, so every
    // session list in the app issues the same shape of request.
    return withReactiveResponse<CoachingSession[]>(
      queryCoachingSessions(
        {
          ...this.args.filters,
          sessionType: this.args.state.sessionType,
          statuses: this.args.state.statuses,
        },
        this.args.state
      )
    );
  }

  updateStatuses = (statuses: CoachingSessionStatus[]) => {
    this.args.setState({
      statuses,
      offset: 0,
    });
  };

  updateSessionType = (sessionType: CoachingSessionType) => {
    this.args.setState({
      sessionType,
      offset: 0,
    });
  };

  updateCoach = (coachUserId: string | null) => {
    this.args.setState({
      coachUserId: coachUserId ?? '',
      offset: 0,
    });
  };

  updateCoachee = (coacheeUserId: string | null) => {
    this.args.setState({
      coacheeUserId: coacheeUserId ?? '',
      offset: 0,
    });
  };

  <template>
    <fieldset ...attributes>
      <legend class="gud-eyebrow">{{@legend}}</legend>
      <div class="row gy-3 mb-3">
        <div class="col-12 col-lg-8">
          <label for="statuses" class="form-label">Status</label>

          <div class="input-group">
            <SelectSessionStatusGroup
              @values={{@state.statuses}}
              @onChange={{this.updateStatuses}}
            />
            <button
              type="button"
              class="btn btn-secondary btn-sm rounded-end-1 px-3"
            >
              <iconify-icon
                icon="ph:dots-three-outline-fill"
                inline
                aria-hidden="true"
              />
              <span class="visually-hidden">More options</span>
              <BsPopover
                @renderInPlace={{false}}
                @placement="bottom"
                @title="Select status"
                class="shadow shadow-lg ll-popover-widest"
              >
                <SelectSessionStatusCheckboxList
                  @values={{@state.statuses}}
                  @onChange={{this.updateStatuses}}
                  class="row row-cols-3 p-3"
                  @itemClass="col mb-2"
                />
              </BsPopover>
            </button>
          </div>
        </div>
        <div class="col-12 col-lg-4">
          <label for="sessionType" class="form-label">Session Type</label>
          <SelectSessionType
            @value={{@state.sessionType}}
            @onChange={{this.updateSessionType}}
          />
        </div>
      </div>
      <div class="row gy-3">
        <div class="col-12 col-lg-6">
          <label for="coachUserId" class="form-label">Coach</label>
          <CoachSearchPicker
            @inputId="coachUserId"
            @userId={{@state.coachUserId}}
            @setUserId={{this.updateCoach}}
            @label="Search coaches by name or email"
            @compact={{true}}
          >
            <:suggestion as |suggestion|>
              {{#if suggestion.meta}}
                {{coachShortDisplayName suggestion.meta}}
              {{else}}
                {{suggestion.user.title}}
              {{/if}}
            </:suggestion>
          </CoachSearchPicker>
        </div>
        <div class="col-12 col-lg-6">
          <label for="coacheeUserId" class="form-label">Coachee</label>
          <UserPicker
            @inputId="coacheeUserId"
            @userId={{@state.coacheeUserId}}
            @setUserId={{this.updateCoachee}}
            @label="Search coachee by name or email"
            @compact={{true}}
          />
        </div>
      </div>
    </fieldset>
    {{yield this.query to="query"}}
  </template>
}
